import { RequestTypeSlugEnum, RequestData } from '@/types/requests';
import { useGetRequestData } from '@/hooks/useGetRequestData';
import { useTranslation } from 'react-i18next';
import {
  taskDescriptionsArgs,
  getTaskDescriptionsText,
  taskPriority,
} from '@/components/Tasks/TaskDescriptionsText';
import { getDisplayFullName } from 'common/utils/getFullName';

type TaskDescriptionData = {
  description: string | null;
  urgencyLabel?: string;
  isLoading: boolean;
  requestedForEmployee: string | null;
};

type TaskDescriptionInput = {
  id: string;
  data: RequestData;
};

const isValidRequestTypeSlug = (slug: string): slug is RequestTypeSlugEnum => {
  return Object.values(RequestTypeSlugEnum).includes(
    slug as RequestTypeSlugEnum
  );
};

/**
 * Custom hook to fetch and format task descriptions with associated metadata.
 *
 * This hook fetches task details using the task ID, processes the data to generate
 * a formatted description based on the request type, and includes additional context
 * such as urgency labels and the employee the task is requested for.
 *
 * @param task - Object containing task ID and request data
 * @param task.id - Unique identifier for the task
 * @param task.data - Request data containing application and expiration details
 *
 * @returns {TaskDescriptionData} Object containing:
 *  - description: Formatted task description or null if unavailable
 *  - urgencyLabel: Priority label for the task
 *  - isLoading: Loading state of the data fetch
 *  - requestedForEmployee: Formatted name of the employee the task is for
 */
export const useTaskDescription = (
  task: TaskDescriptionInput
): TaskDescriptionData => {
  const { t } = useTranslation('requests');
  const { data: details, isLoading } = useGetRequestData({
    requestId: task.id,
    options: {
      includeRequestType: true,
      includeEmployee: true,
      includeApplication: true,
    },
  });

  const taskDescriptionsText = getTaskDescriptionsText(t);

  if (isLoading || !details?.requestType) {
    return {
      description: null,
      urgencyLabel: undefined,
      isLoading,
      requestedForEmployee: null,
    };
  }

  const slug = details.requestType.slug;
  if (!isValidRequestTypeSlug(slug)) {
    console.error(`RequestType ${slug} is not a valid request type slug`);
    return {
      description: null,
      urgencyLabel: undefined,
      isLoading: false,
      requestedForEmployee: null,
    };
  }

  const taskDescription = taskDescriptionsText[slug];
  const urgencyLabel = taskPriority[slug];
  const employee = details.employee;
  const requestedForEmployee = employee ? getDisplayFullName(employee) : null;

  if (!taskDescription) {
    console.error(
      `RequestType ${slug} is missing a description in taskDescriptionsText`
    );
    return {
      description: null,
      urgencyLabel,
      isLoading,
      requestedForEmployee,
    };
  }

  /**
   * Task descriptions can be either static strings or dynamic functions:
   * - String: Used for simple, static task descriptions that don't need contextual information
   * - Function: Used when the description needs to include dynamic data like employee names,
   *   application names, or dates. The function receives context and returns a formatted string.
   */
  if (typeof taskDescription === 'function') {
    const { applicationKey, expiresAt } = task.data;
    // The "application" field can either be an object or a string depending on the request type.
    const appName = details.application?.name;

    // Constructs the arguments for the task description function
    const textFnArgs = taskDescriptionsArgs({
      employeeName: requestedForEmployee || '',
      appName: appName || applicationKey || task.data.application?.name || '',
      dueDate: expiresAt,
    });

    // Checks if the request type has a corresponding function in taskDescriptionsArgs
    if (!textFnArgs[slug]) {
      console.error(`RequestType ${slug} is missing in taskDescriptionsArgs`);
      return {
        description: null,
        urgencyLabel,
        isLoading,
        requestedForEmployee,
      };
    }

    // Return the formatted task description
    return {
      description: taskDescription(textFnArgs[slug]),
      urgencyLabel,
      isLoading,
      requestedForEmployee,
    };
  }

  // Return the static task description
  return {
    description: taskDescription,
    urgencyLabel,
    isLoading,
    requestedForEmployee,
  };
};
